/****
 * Accessibility utility module that manages accessibility settings,
 * applies user preferences to the application UI, and controls
 * Text-to-Speech (TTS) functionality.
 *
 * Provides default accessibility configurations, applies visual
 * accessibility enhancements through CSS classes and variables,
 * manages cursor sizing, font scaling, contrast settings, and
 * enables or disables TTS features such as reading page content,
 * focus interactions, and hover interactions.
 */
import { TTSRuntime } from "./tts";

export type CursorSize = "normal" | "large" | "xlarge";

export type AccessibilitySettings = {
  fontScale: number;
  highContrast: boolean;
  underlineLinks: boolean;
  increasedTextSpacing: boolean;
  reduceMotion: boolean;
  enhancedKeyboardNav: boolean;
  enhancedFocusIndicators: boolean;
  cursorSize: CursorSize;
  ttsOnFocus: boolean;
  ttsOnHover: boolean;
  screenReaderOptimization: boolean;
  dyslexiaFriendlyFont: boolean;
  ttsEnabled: boolean;
  speechRate: number;
};

export const DEFAULTS: AccessibilitySettings = {
  fontScale: 1.0,
  highContrast: false,
  underlineLinks: false,
  increasedTextSpacing: false,
  reduceMotion: false,
  enhancedKeyboardNav: false,
  enhancedFocusIndicators: false,
  cursorSize: "normal",
  ttsOnFocus: false,
  ttsOnHover: false,
  screenReaderOptimization: false,
  dyslexiaFriendlyFont: false,
  ttsEnabled: false,
  speechRate: 1.0,
};

export const MIN_SCALE = 0.75;
export const MAX_SCALE = 1.5;

export const clamp2 = (n: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, Number(n.toFixed(2))));

export const GLOBAL_STORAGE_KEY = "landing.settings.global";

export function applySettingsToCSS(settings: AccessibilitySettings) {
  if (typeof document === "undefined") return;

  const root = document.documentElement;

  root.style.setProperty("--font-scale", String(clamp2(settings.fontScale)));

  toggleClass(root, "a11y-underline-links", settings.underlineLinks);
  toggleClass(root, "a11y-text-spacing", settings.increasedTextSpacing);
  toggleClass(root, "a11y-reduce-motion", settings.reduceMotion);
  toggleClass(root, "a11y-enhanced-focus", settings.enhancedFocusIndicators);
  toggleClass(root, "a11y-sr-optimized", settings.screenReaderOptimization);
  toggleClass(root, "a11y-dyslexia", settings.dyslexiaFriendlyFont);
  toggleClass(root, "a11y-enhanced-keyboard-nav", settings.enhancedKeyboardNav);
  toggleClass(root, "a11y-high-contrast", settings.highContrast);

  root.setAttribute("data-cursor-size", settings.cursorSize);
}

function toggleClass(el: Element, cls: string, on: boolean) {
  if (on) el.classList.add(cls);
  else el.classList.remove(cls);
}

let lastMasterOn = false;

export function enableTTS(settings: AccessibilitySettings) {
  if (typeof window === "undefined") return;

  const rate = Math.max(0.5, Math.min(2.0, Number(settings.speechRate) || 1.0));
  TTSRuntime.setRate(rate);

  if (settings.ttsOnFocus) {
    TTSRuntime.bindFocus();
    TTSRuntime.bindSelection();
  } else {
    TTSRuntime.unbindFocus();
    TTSRuntime.unbindSelection();
  }

  if (settings.ttsOnHover) {
    TTSRuntime.bindHover();
  } else {
    TTSRuntime.unbindHover();
  }

  if (settings.ttsEnabled) {
    if (!lastMasterOn) {
      TTSRuntime.readPage();
    }
    lastMasterOn = true;
  } else {
    TTSRuntime.stop();
    lastMasterOn = false;
  }
}

export { TTSRuntime } from "./tts";
